import React, { useState } from 'react';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/20/solid';
import { API_BASE } from '@/config/api';
import { getAuthHeaders } from '@/utils/apiUtils';

interface QuoteWizardProps {
  onComplete: (data: any) => void;
  onClose: () => void;
}

interface QuoteData {
  garment_type: string;
  service_type: string;
  location_geo?: string;
  photos: string[];
  measurements?: any;
}

const InstantQuoteWizard: React.FC<QuoteWizardProps> = ({ onComplete, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [quoteData, setQuoteData] = useState<QuoteData>({
    garment_type: '',
    service_type: '',
    photos: [],
    measurements: {}
  });

  const garmentTypes = [
    { id: 'suit', name: 'Suit', icon: '👔' },
    { id: 'dress', name: 'Dress', icon: '👗' },
    { id: 'shirt', name: 'Shirt', icon: '👕' },
    { id: 'pants', name: 'Pants', icon: '👖' },
    { id: 'blouse', name: 'Blouse', icon: '👚' },
    { id: 'jacket', name: 'Jacket', icon: '🧥' },
    { id: 'coat', name: 'Coat', icon: '🧥' },
    { id: 'uniform', name: 'Uniform', icon: '👨‍💼' },
    { id: 'other', name: 'Other', icon: '🎽' }
  ];

  const serviceTypes = [
    { id: 'stitching', name: 'Stitching', description: 'Create from scratch' },
    { id: 'alteration', name: 'Alteration', description: 'Modify existing garment' },
    { id: 'repair', name: 'Repair', description: 'Fix damaged garment' },
    { id: 'design', name: 'Design', description: 'Custom design consultation' },
    { id: 'consultation', name: 'Consultation', description: 'Style advice' }
  ];

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      const API_URL = API_BASE; // Use from config instead of hardcoding
      
      const response = await fetch(`${API_URL}/api/quotes/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(quoteData),
      });

      if (!response.ok) {
        throw new Error('Failed to create quote request');
      }

      const result = await response.json();
      onComplete(result);
    } catch (_error) {
      console.error('Error creating quote request:', error);
      alert('Failed to create quote request. Please try again.');
    }
  };

  const updateQuoteData = (field: keyof QuoteData, value: any) => {
    setQuoteData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">What do you need?</h3>
        
        {/* Garment Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Garment Type
          </label>
          <div className="grid grid-cols-3 gap-3">
            {garmentTypes.map((garment) => (
              <button
                key={garment.id}
                onClick={() => updateQuoteData('garment_type', garment.id)}
                className={`p-4 border rounded-lg text-center transition-colors ${
                  quoteData.garment_type === garment.id
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-2xl mb-2">{garment.icon}</div>
                <div className="text-sm font-medium">{garment.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Service Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Service Type
          </label>
          <div className="space-y-3">
            {serviceTypes.map((service) => (
              <button
                key={service.id}
                onClick={() => updateQuoteData('service_type', service.id)}
                className={`w-full p-4 border rounded-lg text-left transition-colors ${
                  quoteData.service_type === service.id
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">{service.name}</div>
                <div className="text-sm text-gray-600">{service.description}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Measurements & Details</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Do you have measurements?
            </label>
            <div className="flex space-x-4">
              <button
                onClick={() => updateQuoteData('measurements', { has_measurements: true })}
                className={`px-4 py-2 border rounded-md ${
                  quoteData.measurements?.has_measurements === true
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                Yes, I have them
              </button>
              <button
                onClick={() => updateQuoteData('measurements', { has_measurements: false })}
                className={`px-4 py-2 border rounded-md ${
                  quoteData.measurements?.has_measurements === false
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                No, I need help
              </button>
            </div>
          </div>

          {quoteData.measurements?.has_measurements === true && (
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                Great! You can share your measurements when you receive quotes from tailors.
              </p>
            </div>
          )}

          {quoteData.measurements?.has_measurements === false && (
            <div className="p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-yellow-800">
                No worries! Tailors can help you with measurements during consultation.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Location & Photos</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Location <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Enter your address or city"
                value={quoteData.location_geo || ''}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                onChange={(e) => updateQuoteData('location_geo', e.target.value)}
                onFocus={() => {
                  // Initialize Google Maps Autocomplete
                  if (window.google && window.google.maps) {
                    const input = document.querySelector('input[placeholder="Enter your address or city"]') as HTMLInputElement;
                    if (input && !input.dataset.autocomplete) {
                      const autocomplete = new window.google.maps.places.Autocomplete(input, {
                        types: ['geocode'],
                        componentRestrictions: { country: 'IN' } // Restrict to India
                      });
                      autocomplete.addListener('place_changed', () => {
                        const place = autocomplete.getPlace();
                        if (place.geometry && place.geometry.location) {
                          const lat = place.geometry.location.lat();
                          const lng = place.geometry.location.lng();
                          updateQuoteData('location_geo', `${lat},${lng}`);
                        }
                      });
                      input.dataset.autocomplete = 'true';
                    }
                  }
                }}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Location is required to help tailors provide accurate quotes and pickup services
            </p>
            {!quoteData.location_geo && (
              <p className="text-xs text-red-500 mt-1">
                Please enter your location to continue
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Photos (optional)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <div className="text-gray-500">
                <svg className="mx-auto h-12 w-12 mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <p>Click to upload photos or drag and drop</p>
                <p className="text-xs">PNG, JPG up to 10MB</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return quoteData.garment_type && quoteData.service_type;
      case 2:
        return quoteData.measurements?.has_measurements !== undefined;
      case 3:
        return quoteData.location_geo && quoteData.location_geo.trim() !== ''; // Location is now mandatory
      default:
        return false;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Get Instant Quote</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center mt-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-1 mx-2 ${
                    step < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex justify-between">
            <button
              onClick={handlePrev}
              disabled={currentStep === 1}
              className={`flex items-center px-4 py-2 border rounded-md ${
                currentStep === 1
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <ChevronLeftIcon className="h-4 w-4 mr-2" />
              Previous
            </button>

            {currentStep < 3 ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`flex items-center px-4 py-2 border rounded-md ${
                  canProceed()
                    ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'
                    : 'border-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next
                <ChevronRightIcon className="h-4 w-4 ml-2" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="flex items-center px-6 py-2 border border-blue-600 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Get Quote
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstantQuoteWizard;
